import { createContext, useContext, useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [lastMessage, setLastMessage] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  const getWebSocketUrl = () => {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return `${protocol}//${host}/ws/webclient`;
  };

  const getTokensFromCookies = () => {
    const jwtToken = Cookies.get("sensolus-token");
    const csrfToken = Cookies.get("_csrf");

    return { jwtToken, csrfToken };
  };

  const connect = (jwtToken, csrfToken) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    // Use provided tokens or fall back to cookies
    let actualJwtToken = jwtToken;
    let actualCsrfToken = csrfToken;

    if (!actualJwtToken || !actualCsrfToken) {
      const cookieTokens = getTokensFromCookies();
      actualJwtToken = actualJwtToken || cookieTokens.jwtToken;
      actualCsrfToken = actualCsrfToken || cookieTokens.csrfToken;
    }

    if (!actualJwtToken || !actualCsrfToken) {
      console.warn("Cannot connect WebSocket: Missing authentication tokens");
      setConnectionStatus("missing_tokens");
      return;
    }

    try {
      const wsUrl = `${getWebSocketUrl()}?token=${encodeURIComponent(
        actualJwtToken
      )}&csrf=${encodeURIComponent(actualCsrfToken)}`;

      console.log("Connecting to WebSocket " + wsUrl);
      setConnectionStatus("connecting");

      const ws = new WebSocket(wsUrl);

      ws.onopen = (event) => {
        console.log("WebSocket connected successfully");
        setIsConnected(true);
        setConnectionStatus("connected");
        reconnectAttempts.current = 0;

        // Clear any pending reconnect attempts
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        console.log("WebSocket message received:", event.data);
        try {
          const message = JSON.parse(event.data);

          // Handle session info message specially
          if (message.type === "session_info") {
            console.log("Received session ID:", message.sessionId);
            setSessionId(message.sessionId);
          }

          setLastMessage(message);
        } catch (error) {
          console.error("Failed to parse WebSocket message:", error);
          setLastMessage({ type: "raw", data: event.data });
        }
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setIsConnected(false);
        setConnectionStatus("disconnected");
        websocketRef.current = null;

        // Attempt reconnection if it wasn't a clean close and we have tokens
        if (
          event.code !== 1000 &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          const {
            jwtToken: currentJwtToken,
            csrfToken: currentCsrfToken,
          } = getTokensFromCookies();

          if (currentJwtToken && currentCsrfToken) {
            reconnectAttempts.current++;
            console.log(
              `Attempting to reconnect... (${reconnectAttempts.current}/${maxReconnectAttempts})`
            );
            setConnectionStatus("reconnecting");

            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, reconnectDelay);
          } else {
            console.log("Not reconnecting: Authentication tokens missing");
            setConnectionStatus("missing_tokens");
          }
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.log("Max reconnection attempts reached");
          setConnectionStatus("failed");
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setConnectionStatus("error");
      };

      websocketRef.current = ws;
    } catch (error) {
      console.error("Failed to create WebSocket connection:", error);
      setConnectionStatus("error");
    }
  };

  const disconnect = () => {
    console.log("Disconnecting WebSocket...");

    // Clear any pending reconnect attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset reconnect attempts
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection

    if (websocketRef.current) {
      websocketRef.current.close(1000, "User logout"); // Clean close
      websocketRef.current = null;
    }

    setIsConnected(false);
    setConnectionStatus("disconnected");
    setLastMessage(null);
    setSessionId(null);
  };

  const sendMessage = (message) => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      websocketRef.current.send(messageStr);
      console.log("WebSocket message sent:", messageStr);
      return true;
    } else {
      console.warn("Cannot send message: WebSocket not connected");
      return false;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, []);

  const value = {
    isConnected,
    connectionStatus,
    lastMessage,
    sessionId,
    connect,
    disconnect,
    sendMessage,
    reconnectAttempts: reconnectAttempts.current,
    maxReconnectAttempts,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
